// Admin.js - Admin dashboard for user management
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import DocumentAccessModal from '../components/DocumentAccessModal';
import { 
  AlertCircle, 
  Loader2, 
  Shield, 
  ShieldOff, 
  CheckCircle, 
  Clock,
  Users,
  ArrowLeft,
  UserCircle,
  Mail,
  Calendar,
  FileText,
  X,
  Check,
  Building2,
  Globe,
  Lock
} from 'lucide-react';

function Admin() {
  const [users, setUsers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [accessModalUser, setAccessModalUser] = useState(null);
  
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const fetchUsers = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${config.API_BASE_URL}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 403) {
        setError('Admin access required');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.users);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const fetchDocuments = useCallback(async () => {
    if (!token) return;
    
    setIsLoadingDocuments(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/admin/documents`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    } finally {
      setIsLoadingDocuments(false);
    }
  }, [token]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Check if user is admin
    if (user && !user.is_admin) {
      setError('Admin access required');
      setIsLoading(false);
      return;
    }
    
    fetchUsers();
    fetchDocuments();
  }, [isAuthenticated, user, navigate, fetchUsers, fetchDocuments]);

  const handleToggleAdmin = async (userId, currentStatus) => {
    setActionLoading(userId);
    try {
      const response = await fetch(`${config.API_BASE_URL}/admin/users/${userId}/toggle-admin`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_admin: !currentStatus })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update admin status');
      }

      // Refresh the user list
      await fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveUser = async (userId) => {
    setActionLoading(userId);
    try {
      const response = await fetch(`${config.API_BASE_URL}/admin/users/${userId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to approve user');
      }

      // Refresh the user list
      await fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleShowPdf = async (userId, currentStatus) => {
    setActionLoading(userId);
    try {
      const response = await fetch(`${config.API_BASE_URL}/admin/users/${userId}/access`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ show_pdf: !currentStatus })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update PDF visibility');
      }

      await fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateAccess = async (userId, allowedDocuments, organization, promptConfig, canSelectPrompt, canCreatePrompts, showPdf) => {
    setActionLoading(userId);
    try {
      const response = await fetch(`${config.API_BASE_URL}/admin/users/${userId}/access`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          allowed_documents: allowedDocuments,
          organization: organization,
          prompt_config: promptConfig,
          can_select_prompt: canSelectPrompt,
          can_create_prompts: canCreatePrompts,
          show_pdf: showPdf
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update access');
      }

      // Refresh the user list
      await fetchUsers();
      setAccessModalUser(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAccessLabel = (u) => {
    if (u.is_admin) return { label: 'Full Access', type: 'admin' };
    if (u.allowed_documents && u.allowed_documents.length > 0) {
      return { label: `${u.allowed_documents.length} Document${u.allowed_documents.length > 1 ? 's' : ''}`, type: 'custom' };
    }
    return { label: 'Public Only', type: 'public' };
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-purple-50/30 to-blue-50/30">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-kaleido-purple/20 animate-ping" />
            <Loader2 className="h-10 w-10 animate-spin text-kaleido-purple relative z-10" />
          </div>
          <p className="text-kaleido-gray-500 font-medium">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error === 'Admin access required') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-purple-50/30 to-blue-50/30">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <ShieldOff className="h-8 w-8 text-red-500" />
            </div>
            <CardTitle className="text-xl text-kaleido-gray-900">Access Denied</CardTitle>
            <CardDescription className="text-kaleido-gray-500">
              You do not have permission to access this page.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center pt-2">
            <Button onClick={() => navigate('/app')} variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pendingUsers = users.filter(u => !u.is_approved);
  const approvedUsers = users.filter(u => u.is_approved);

  return (
    <div className="min-h-screen h-screen overflow-y-auto bg-gradient-to-br from-slate-50 via-purple-50/30 to-blue-50/30">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-kaleido-gray-100">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/app')} variant="ghost" size="sm" className="gap-1.5 text-kaleido-gray-500 hover:text-kaleido-gray-900 h-8 px-2">
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline text-sm font-medium">Back</span>
              </Button>
              <div className="h-5 w-px bg-kaleido-gray-200" />
              <div className="flex items-center gap-2.5 sm:gap-3.5">
                <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-lg sm:rounded-xl bg-gradient-to-br from-kaleido-purple to-kaleido-blue flex items-center justify-center shadow-lg ring-1 ring-black/10">
                  <Users className="h-4 w-4 sm:h-[22px] sm:w-[22px] text-white" />
                </div>
                <div className="flex flex-col">
                  <h1 className="text-sm sm:text-base font-semibold text-kaleido-gray-900 leading-none">
                    User Management
                  </h1>
                  <p className="text-[10px] text-kaleido-gray-500 mt-1 hidden sm:block font-medium tracking-wide">
                    MANAGE ACCOUNTS & ACCESS
                  </p>
                </div>
              </div>
            </div>
            <Badge variant="secondary" className="bg-kaleido-purple/5 text-kaleido-purple border border-kaleido-purple/10 font-medium text-[10px] sm:text-xs px-2 sm:px-2.5 py-0.5 rounded-full">
              {users.length} <span className="hidden sm:inline">Users</span>
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {error && error !== 'Admin access required' && (
          <Alert variant="destructive" className="mb-4 border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4 sm:mb-5">
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                <div>
                  <p className="text-[11px] sm:text-xs text-kaleido-gray-500 font-medium">Total</p>
                  <p className="text-xl sm:text-2xl font-bold text-kaleido-gray-900">{users.length}</p>
                </div>
                <div className="hidden sm:flex w-9 h-9 rounded-lg bg-gradient-to-br from-kaleido-purple/10 to-kaleido-blue/10 items-center justify-center">
                  <Users className="h-4 w-4 text-kaleido-purple" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                <div>
                  <p className="text-[11px] sm:text-xs text-kaleido-gray-500 font-medium">Approved</p>
                  <p className="text-xl sm:text-2xl font-bold text-emerald-600">{approvedUsers.length}</p>
                </div>
                <div className="hidden sm:flex w-9 h-9 rounded-lg bg-emerald-50 items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                <div>
                  <p className="text-[11px] sm:text-xs text-kaleido-gray-500 font-medium">Pending</p>
                  <p className="text-xl sm:text-2xl font-bold text-amber-600">{pendingUsers.length}</p>
                </div>
                <div className="hidden sm:flex w-9 h-9 rounded-lg bg-amber-50 items-center justify-center">
                  <Clock className="h-4 w-4 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Users Section */}
        {pendingUsers.length > 0 && (
          <Card className="border-0 shadow-md mb-4 sm:mb-5 overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100 py-3 px-4">
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-md bg-amber-100 flex items-center justify-center">
                  <Clock className="h-3.5 w-3.5 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-sm font-semibold text-kaleido-gray-900">Pending Approval</CardTitle>
                  <CardDescription className="text-xs text-amber-700">
                    {pendingUsers.length} user{pendingUsers.length !== 1 ? 's' : ''} waiting for approval
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-kaleido-gray-100">
                {pendingUsers.map((u) => (
                  <UserRow 
                    key={u.id} 
                    u={u} 
                    user={user}
                    actionLoading={actionLoading}
                    onApprove={handleApproveUser}
                    onToggleAdmin={handleToggleAdmin}
                    onToggleShowPdf={handleToggleShowPdf}
                    onEditAccess={setAccessModalUser}
                    formatDate={formatDate}
                    getAccessLabel={getAccessLabel}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* All Users Section */}
        <Card className="border-0 shadow-md overflow-hidden">
          <CardHeader className="border-b border-kaleido-gray-100 bg-gradient-to-r from-kaleido-purple/5 to-kaleido-blue/5 py-3 px-4">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-md bg-gradient-to-br from-kaleido-purple to-kaleido-blue flex items-center justify-center shadow-sm">
                <Users className="h-3.5 w-3.5 text-white" />
              </div>
              <div>
                <CardTitle className="text-sm font-semibold text-kaleido-gray-900">All Users</CardTitle>
                <CardDescription className="text-xs">View and manage all user accounts</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {users.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-kaleido-gray-500">
                <Users className="h-10 w-10 mb-3 opacity-30" />
                <p className="font-medium text-sm">No users found</p>
                <p className="text-xs">Users will appear here once they register</p>
              </div>
            ) : (
              <div className="divide-y divide-kaleido-gray-100">
                {users.map((u) => (
                  <UserRow 
                    key={u.id} 
                    u={u} 
                    user={user}
                    actionLoading={actionLoading}
                    onApprove={handleApproveUser}
                    onToggleAdmin={handleToggleAdmin}
                    onToggleShowPdf={handleToggleShowPdf}
                    onEditAccess={setAccessModalUser}
                    formatDate={formatDate}
                    getAccessLabel={getAccessLabel}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Access Control Modal */}
      {accessModalUser && (
        <DocumentAccessModal
          user={accessModalUser}
          documents={documents}
          onClose={() => setAccessModalUser(null)}
          onSave={handleUpdateAccess}
          isLoading={actionLoading === accessModalUser.id}
          isLoadingDocuments={isLoadingDocuments}
        />
      )}
    </div>
  );
}

// Extracted UserRow component for cleaner code
function UserRow({ u, user, actionLoading, onApprove, onToggleAdmin, onToggleShowPdf, onEditAccess, formatDate, getAccessLabel }) {
  const isCurrentUser = u.id === user?.id;
  const showApproveButton = !u.is_approved;
  const showAdminButton = !isCurrentUser;
  const accessInfo = getAccessLabel(u);
  
  return (
    <div className="px-3 py-2 hover:bg-kaleido-gray-50/50 transition-colors">
      {/* Mobile Layout */}
      <div className="md:hidden">
        {/* User Info + Badges in single row */}
        <div className="flex items-start gap-2">
          {/* Avatar */}
          <div className="flex-shrink-0 pt-0.5">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
              u.is_admin 
                ? 'bg-gradient-to-br from-kaleido-purple to-kaleido-blue shadow-sm' 
                : 'bg-kaleido-gray-100 border border-kaleido-gray-200'
            }`}>
              <UserCircle className={`h-3.5 w-3.5 ${u.is_admin ? 'text-white' : 'text-kaleido-gray-400'}`} />
            </div>
          </div>
          
          {/* Content */}
          <div className="min-w-0 flex-1 space-y-1.5">
            {/* Name & Email */}
            <div>
              <div className="flex items-center gap-1">
                <p className="font-medium text-xs text-kaleido-gray-900 truncate">
                  {u.display_name}
                </p>
                {isCurrentUser && (
                  <span className="text-[8px] px-1 py-0.5 rounded bg-kaleido-purple/10 text-kaleido-purple font-medium flex-shrink-0">
                    You
                  </span>
                )}
              </div>
              <p className="text-[10px] text-kaleido-gray-500 truncate">{u.email}</p>
            </div>

            {/* Badges Grid - 2 columns on small screens */}
            <div className="grid grid-cols-2 gap-1">
              {/* Approval Status */}
              <div className={`h-5 px-1.5 rounded flex items-center justify-center gap-0.5 text-[9px] font-medium border ${
                u.is_approved 
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                  : 'bg-amber-50 text-amber-700 border-amber-200'
              }`}>
                {u.is_approved ? (
                  <CheckCircle className="h-2.5 w-2.5 flex-shrink-0" />
                ) : (
                  <Clock className="h-2.5 w-2.5 flex-shrink-0" />
                )}
                <span>{u.is_approved ? 'Approved' : 'Pending'}</span>
              </div>

              {/* Admin Status - Clickable to toggle */}
              {showAdminButton ? (
                <button
                  type="button"
                  onClick={() => onToggleAdmin(u.id, u.is_admin)}
                  disabled={actionLoading === u.id}
                  className={`h-5 px-1.5 rounded flex items-center justify-center gap-0.5 text-[9px] font-medium border transition-colors disabled:opacity-50 ${
                    u.is_admin 
                      ? 'bg-kaleido-purple/10 text-kaleido-purple border-kaleido-purple/20 hover:bg-red-50 hover:text-red-600 hover:border-red-200' 
                      : 'bg-kaleido-gray-50 text-kaleido-gray-500 border-kaleido-gray-200 hover:bg-kaleido-purple/10 hover:text-kaleido-purple hover:border-kaleido-purple/20'
                  }`}
                  title={u.is_admin ? 'Click to revoke admin' : 'Click to make admin'}
                >
                  {actionLoading === u.id ? (
                    <Loader2 className="h-2.5 w-2.5 animate-spin" />
                  ) : (
                    <>
                      <Shield className="h-2.5 w-2.5 flex-shrink-0" />
                      <span>{u.is_admin ? 'Admin' : 'Make Admin'}</span>
                    </>
                  )}
                </button>
              ) : u.is_admin ? (
                <div className="h-5 px-1.5 rounded flex items-center justify-center gap-0.5 text-[9px] font-medium bg-kaleido-purple/10 text-kaleido-purple border border-kaleido-purple/20">
                  <Shield className="h-2.5 w-2.5 flex-shrink-0" />
                  <span>Admin</span>
                </div>
              ) : (
                <div className="h-5 px-1.5 rounded flex items-center justify-center gap-0.5 text-[9px] font-medium bg-kaleido-gray-50 text-kaleido-gray-400 border border-kaleido-gray-200">
                  <Shield className="h-2.5 w-2.5 flex-shrink-0" />
                  <span>User</span>
                </div>
              )}

              {/* Document Access */}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!u.is_admin) {
                    onEditAccess(u);
                  }
                }}
                disabled={u.is_admin}
                className={`h-5 px-1.5 rounded flex items-center justify-center gap-0.5 text-[9px] font-medium border transition-colors ${
                  accessInfo.type === 'admin' 
                    ? 'bg-gradient-to-r from-kaleido-purple/10 to-kaleido-blue/10 text-kaleido-purple border-kaleido-purple/20 cursor-default'
                    : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 cursor-pointer active:bg-blue-200'
                }`}
                title={u.is_admin ? 'Admins have full access' : 'Click to edit access'}
              >
                {accessInfo.type === 'admin' ? (
                  <Globe className="h-2.5 w-2.5 flex-shrink-0" />
                ) : accessInfo.type === 'custom' ? (
                  <FileText className="h-2.5 w-2.5 flex-shrink-0" />
                ) : (
                  <Lock className="h-2.5 w-2.5 flex-shrink-0" />
                )}
                <span>{accessInfo.label}</span>
              </button>

              {/* Show PDF Toggle */}
              <button
                type="button"
                onClick={() => onToggleShowPdf(u.id, u.show_pdf !== false)}
                disabled={actionLoading === u.id}
                className={`h-5 px-1.5 rounded flex items-center justify-center gap-0.5 text-[9px] font-medium border transition-colors disabled:opacity-50 ${
                  u.show_pdf !== false
                    ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200'
                    : 'bg-kaleido-gray-50 text-kaleido-gray-400 border-kaleido-gray-200 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200'
                }`}
                title={u.show_pdf !== false ? 'Click to hide PDFs for this user' : 'Click to show PDFs for this user'}
              >
                {actionLoading === u.id ? (
                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                ) : (
                  <>
                    <FileText className="h-2.5 w-2.5 flex-shrink-0" />
                    <span>{u.show_pdf !== false ? 'PDF On' : 'PDF Off'}</span>
                  </>
                )}
              </button>

              {/* Date or Approve button */}
              {showApproveButton ? (
                <button
                  type="button"
                  className="h-5 px-1.5 rounded flex items-center justify-center gap-0.5 text-[9px] font-medium border transition-colors bg-emerald-100 text-emerald-700 border-emerald-300 hover:bg-emerald-200 disabled:opacity-50"
                  onClick={() => onApprove(u.id)}
                  disabled={actionLoading === u.id}
                >
                  {actionLoading === u.id ? (
                    <Loader2 className="h-2.5 w-2.5 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="h-2.5 w-2.5 flex-shrink-0" />
                      <span>Approve</span>
                    </>
                  )}
                </button>
              ) : (
                <div className="h-5 px-1.5 rounded flex items-center justify-center gap-0.5 text-[9px] font-medium bg-kaleido-gray-50 text-kaleido-gray-400 border border-kaleido-gray-100">
                  <Calendar className="h-2.5 w-2.5 flex-shrink-0" />
                  <span>{formatDate(u.created_at).split(',')[0]}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Layout - CSS Grid with flexible columns */}
      <div className="hidden md:grid grid-cols-[2.5fr_1fr_1fr_1fr_0.8fr_1fr] gap-2 items-center">
        {/* Column 1: User Info */}
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex-shrink-0">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
              u.is_admin 
                ? 'bg-gradient-to-br from-kaleido-purple to-kaleido-blue shadow-sm' 
                : 'bg-kaleido-gray-100 border border-kaleido-gray-200'
            }`}>
              <UserCircle className={`h-3.5 w-3.5 ${u.is_admin ? 'text-white' : 'text-kaleido-gray-400'}`} />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <p className="font-medium text-xs text-kaleido-gray-900 truncate">
                {u.display_name}
              </p>
              {isCurrentUser && (
                <span className="text-[8px] px-1 py-0.5 rounded bg-kaleido-purple/10 text-kaleido-purple font-medium flex-shrink-0">
                  You
                </span>
              )}
            </div>
            <p className="text-[10px] text-kaleido-gray-500 truncate">{u.email}</p>
          </div>
        </div>

        {/* Column 2: Approval Status */}
        <div className={`h-6 px-2 rounded-full flex items-center justify-center gap-1 text-[10px] font-medium border ${
          u.is_approved 
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
            : 'bg-amber-50 text-amber-700 border-amber-200'
        }`}>
          {u.is_approved ? (
            <CheckCircle className="h-3 w-3 flex-shrink-0" />
          ) : (
            <Clock className="h-3 w-3 flex-shrink-0" />
          )}
          <span>{u.is_approved ? 'Approved' : 'Pending'}</span>
        </div>

        {/* Column 3: Admin Status - Clickable to toggle */}
        {showAdminButton ? (
          <button
            type="button"
            onClick={() => onToggleAdmin(u.id, u.is_admin)}
            disabled={actionLoading === u.id}
            className={`h-6 px-2 rounded-full flex items-center justify-center gap-1 text-[10px] font-medium border transition-colors disabled:opacity-50 ${
              u.is_admin 
                ? 'bg-kaleido-purple/10 text-kaleido-purple border-kaleido-purple/20 hover:bg-red-50 hover:text-red-600 hover:border-red-200' 
                : 'bg-kaleido-gray-50 text-kaleido-gray-500 border-kaleido-gray-200 hover:bg-kaleido-purple/10 hover:text-kaleido-purple hover:border-kaleido-purple/20'
            }`}
            title={u.is_admin ? 'Click to revoke admin' : 'Click to make admin'}
          >
            {actionLoading === u.id ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <>
                <Shield className="h-3 w-3 flex-shrink-0" />
                <span>{u.is_admin ? 'Admin' : 'Make Admin'}</span>
              </>
            )}
          </button>
        ) : u.is_admin ? (
          <div className="h-6 px-2 rounded-full flex items-center justify-center gap-1 text-[10px] font-medium bg-kaleido-purple/10 text-kaleido-purple border border-kaleido-purple/20">
            <Shield className="h-3 w-3 flex-shrink-0" />
            <span>Admin</span>
          </div>
        ) : (
          <div className="h-6 px-2 rounded-full flex items-center justify-center gap-1 text-[10px] font-medium bg-kaleido-gray-50 text-kaleido-gray-400 border border-kaleido-gray-200">
            <Shield className="h-3 w-3 flex-shrink-0" />
            <span>User</span>
          </div>
        )}

        {/* Column 4: Document Access */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!u.is_admin) {
              onEditAccess(u);
            }
          }}
          disabled={u.is_admin}
          className={`h-6 px-2 rounded-full flex items-center justify-center gap-1 text-[10px] font-medium border transition-colors ${
            accessInfo.type === 'admin' 
              ? 'bg-gradient-to-r from-kaleido-purple/10 to-kaleido-blue/10 text-kaleido-purple border-kaleido-purple/20 cursor-default'
              : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 cursor-pointer active:bg-blue-200'
          }`}
          title={u.is_admin ? 'Admins have full access' : 'Click to edit access'}
        >
          {accessInfo.type === 'admin' ? (
            <Globe className="h-3 w-3 flex-shrink-0" />
          ) : accessInfo.type === 'custom' ? (
            <FileText className="h-3 w-3 flex-shrink-0" />
          ) : (
            <Lock className="h-3 w-3 flex-shrink-0" />
          )}
          <span>{accessInfo.label}</span>
        </button>

        {/* Column 5: Show PDF Toggle */}
        <button
          type="button"
          onClick={() => onToggleShowPdf(u.id, u.show_pdf !== false)}
          disabled={actionLoading === u.id}
          className={`h-6 px-2 rounded-full flex items-center justify-center gap-1 text-[10px] font-medium border transition-colors disabled:opacity-50 ${
            u.show_pdf !== false
              ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200'
              : 'bg-kaleido-gray-50 text-kaleido-gray-400 border-kaleido-gray-200 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200'
          }`}
          title={u.show_pdf !== false ? 'Click to hide PDFs for this user' : 'Click to show PDFs for this user'}
        >
          {actionLoading === u.id ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <>
              <FileText className="h-3 w-3 flex-shrink-0" />
              <span>{u.show_pdf !== false ? 'PDF On' : 'PDF Off'}</span>
            </>
          )}
        </button>

        {/* Column 6: Date or Approve button */}
        {showApproveButton ? (
          <button
            type="button"
            className="h-6 px-2 rounded-full flex items-center justify-center gap-1 text-[10px] font-medium border transition-colors bg-emerald-100 text-emerald-700 border-emerald-300 hover:bg-emerald-200 disabled:opacity-50"
            onClick={() => onApprove(u.id)}
            disabled={actionLoading === u.id}
          >
            {actionLoading === u.id ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <>
                <CheckCircle className="h-3 w-3 flex-shrink-0" />
                <span>Approve</span>
              </>
            )}
          </button>
        ) : (
          <div className="h-6 px-2 rounded-full flex items-center justify-center gap-1 text-[10px] font-medium bg-kaleido-gray-50 text-kaleido-gray-500 border border-kaleido-gray-200">
            <Calendar className="h-3 w-3 flex-shrink-0" />
            <span>{formatDate(u.created_at).split(',')[0]}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default Admin;
